import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Button, EditButton, FormWrapper, ImageUploadModal, SubmitButton, ViewMediaModal } from "../../components";

function UserDetails({ parentName, title, showEditButton, handleEdit, handleAadharUpload, handlePanUpload, data }) {
  const [editable, setEditable] = useState(false);
  const [formData, setFormData] = useState({});
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [imageUploadOptions, setImageUploadOptions] = useState({});
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    if (editable) {
      if (parentName === "Agent") {
        setFormData({
          id: data.id,
          name: data.name,
          contact: data.contact,
          email: data.email,
          age: data.age,
          gender: data.gender,
          address: data.address,
          agent_id: data.agent_id,
          maha_rera_number: data.maha_rera_number,
          aadhar_document_id: data.aadhar_document_id,
          aadhar_document_url: data.aadhar_document_url,
          pan_document_id: data.pan_document_id,
          pan_document_url: data.pan_document_url,
          photo_id: data.photo_id,
          photo_url: data.photo_url,
        });
      } else {
        setFormData({
          id: data.id,
          name: data.name,
          contact: data.contact,
          email: data.email,
          age: data.age,
          gender: data.gender,
          occupation: data.occupation,
          address: data.address,
          aadhar_document_id: data.aadhar_document_id,
          aadhar_document_url: data.aadhar_document_url,
          pan_document_id: data.pan_document_id,
          pan_document_url: data.pan_document_url,
          photo: data.photo,
          photo_id: data.photo_id,
          photo_url: data.photo_url,
        });
      }
    }
  }, [editable, data, parentName]);

  const handleInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "capitalize")
      newValue = value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
    if (useTextCase === "lowercase") newValue = value.toLowerCase();

    setFormData({ ...formData, [name]: newValue });
    if (editable) setEditedData((prevData) => ({ ...prevData, [name]: newValue }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (editedData.email && !emailRegex.test(editedData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    handleEdit(editedData);
    setEditable(false);
  };

  const handleImageUpload = (imageId, url, key) => {
    if (editable) {
      setFormData({ ...formData, [`${key}_id`]: imageId, [`${key}_url`]: url });
      setEditedData((prevData) => ({ ...prevData, [`${key}_id`]: imageId }));
    } else {
      if (key === "aadhar_document") handleAadharUpload(imageId, url);
      if (key === "pan_document") handlePanUpload(imageId, url);
    }
    setShowImageUploadModal(false);
  };

  const handlePhotoReupload = () => {
    setImageUploadOptions({
      title: `Re-upload Photo`,
      handleUpload: (id, url) => handleImageUpload(id, url, "photo"),
    });
    setShowImageUploadModal(true);
  };
  const handleAadharReupload = () => {
    setImageUploadOptions({
      title: `Re-upload Aadhar Card`,
      handleUpload: (id, url) => handleImageUpload(id, url, "aadhar_document"),
    });
    setShowImageUploadModal(true);
  };
  const handlePanReupload = () => {
    setImageUploadOptions({
      title: `Re-upload PAN Card`,
      handleUpload: (id, url) => handleImageUpload(id, url, "pan_document"),
    });
    setShowImageUploadModal(true);
  };

  const [showViewMediaModal, setShowViewMediaModal] = useState(false);
  const [viewMediaOptions, setViewMediaOptions] = useState({});
  const handleViewAadhar = () => {
    const url = editable ? formData.aadhar_document_url : data.aadhar_document_url;
    setViewMediaOptions({
      title: "Aadhar Card",
      url: [url],
      mediaType: "image",
    });
    setShowViewMediaModal(true);
  };
  const handleViewPan = () => {
    setViewMediaOptions({
      title: "PAN Card",
      url: [editable ? formData.pan_document_url : data.pan_document_url],
      mediaType: "image",
    });
    setShowViewMediaModal(true);
  };

  if (editable) {
    return (
      <>
        <FormWrapper id="user_details_form" onSubmit={handleSave}>
          <section className="section_wrapper">
            <div className="section_title_wrapper">
              <h2 className="section_title">{title ?? `details`}</h2>
            </div>
            <div className="row spread">
              <div className="column shrink">
                {formData.photo_url ? (
                  <div className="photo_wrapper">
                    <img src={formData.photo_url} alt={formData.name} className="photo" />
                  </div>
                ) : (
                  <div className="photo_wrapper_empty">
                    <Icon className="photo_placeholder" icon="iconamoon:profile-fill" />
                  </div>
                )}

                <Button className="upload" onClick={handlePhotoReupload}>
                  {formData.photo_url ? "Re-upload" : "Upload"}
                </Button>
              </div>
              <div className="section grid_3 full">
                <div className="column">
                  {parentName === "Agent" && (
                    <div className="input_wrapper">
                      <label htmlFor="agent_id" className="label gray">
                        Agent Company ID
                      </label>
                      <input
                        type="text"
                        id="agent_id"
                        name="agent_id"
                        value={formData.agent_id ?? formData.id}
                        onChange={handleInputChange}
                        className="input input_round"
                        readOnly
                      />
                    </div>
                  )}
                  <div className="input_wrapper">
                    <label htmlFor="name" className="label gray">
                      {parentName} name (as per Aadhaar)
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      required
                      onChange={(e) => handleInputChange(e, "capitalize")}
                      className="input input_round"
                      pattern="([a-zA-Z\s+]+)"
                      title="Only alphabets are allowed"
                      maxLength={50}
                    />
                  </div>
                  <div className="input_wrapper">
                    <label htmlFor="contact" className="label gray">
                      Contact number
                    </label>
                    <input
                      type="tel"
                      id="contact"
                      name="contact"
                      value={formData.contact}
                      onChange={handleInputChange}
                      pattern="[0-9]{10}"
                      minLength={10}
                      maxLength={10}
                      title="Only numbers are allowed"
                      className="input input_round"
                      required
                    />
                  </div>
                  <div className="input_wrapper">
                    <label htmlFor="email" className="label gray">
                      Email ID
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      onChange={handleInputChange}
                      value={formData.email}
                      className="input input_round"
                      required
                    />
                  </div>
                  {parentName !== "Agent" && (
                    <div className="row">
                      <div className="input_wrapper full">
                        <label htmlFor="address" className="label gray">
                          Age
                        </label>
                        <input
                          type="number"
                          pattern="[0-9]*"
                          id="age"
                          name="age"
                          value={formData.age}
                          onChange={handleInputChange}
                          className="input input_round"
                          required={parentName !== "Agent"}
                          min={18}
                          max={100}
                        />
                      </div>
                      <div className="input_wrapper full">
                        <label htmlFor="gender" className="label gray">
                          Gender
                        </label>
                        <input
                          type="text"
                          id="gender"
                          name="gender"
                          value={formData.gender}
                          className="input input_round"
                          required={parentName !== "Agent"}
                          onChange={(e) => handleInputChange(e, "lowercase")}
                          pattern="male|female|other"
                          title="Only 'male', 'female' and 'other' are allowed"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="vertical_divider"></div>
                <div className="column">
                  {parentName === "Agent" ? (
                    <div className="row">
                      <div className="input_wrapper full">
                        <label htmlFor="address" className="label gray">
                          Age
                        </label>
                        <input
                          type="number"
                          pattern="[0-9]*"
                          id="age"
                          name="age"
                          value={formData.age}
                          onChange={handleInputChange}
                          className="input input_round"
                          min={18}
                          max={100}
                          required={parentName === "Agent"}
                        />
                      </div>
                      <div className="input_wrapper full">
                        <label htmlFor="gender" className="label gray">
                          Gender
                        </label>
                        <input
                          type="text"
                          id="gender"
                          name="gender"
                          value={formData.gender}
                          className="input input_round"
                          onChange={(e) => handleInputChange(e, "lowercase")}
                          pattern="male|female|other"
                          title="Only 'male', 'female' and 'other' are allowed"
                          required={parentName === "Agent"}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="input_wrapper">
                      <label htmlFor="occupation" className="label gray">
                        Occupation
                      </label>
                      <input
                        type="text"
                        id="occupation"
                        name="occupation"
                        value={formData.occupation}
                        onChange={(e) => handleInputChange(e, "capitalize")}
                        className="input input_round"
                        required={parentName !== "Agent"}
                        pattern="([a-zA-Z\s+]+)"
                        title="Only alphabets are allowed"
                        maxLength={50}
                      />
                    </div>
                  )}
                  <div className="input_wrapper">
                    <label htmlFor="address" className="label gray">
                      Address (as per ID)
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="input input_round"
                      required
                    />
                  </div>
                  <div className="row">
                    <span className="label gray">Aadhaar number</span>
                    {formData.aadhar_document_url ? (
                      <Button className="ghost_gray" onClick={handleViewAadhar}>
                        preview
                      </Button>
                    ) : (
                      <span className="small gray">No image uploaded</span>
                    )}
                    <Button onClick={handleAadharReupload} className="upload">
                      Upload
                    </Button>
                  </div>
                  <div className="row">
                    <span className="label gray">PAN number</span>
                    {formData.pan_document_url ? (
                      <Button className="ghost_gray" onClick={handleViewPan}>
                        preview
                      </Button>
                    ) : (
                      <span className="small gray">No image uploaded</span>
                    )}
                    <Button onClick={handlePanReupload} className="upload">
                      Upload
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="row end">
              <Button className="ghost_gray" onClick={() => setEditable(false)}>
                Cancel
              </Button>
              <SubmitButton className="submit" form="user_details_form">
                Save
              </SubmitButton>
            </div>
          </section>
        </FormWrapper>
        {showImageUploadModal &&
          createPortal(
            <ImageUploadModal
              title={imageUploadOptions.title}
              handleClose={() => setShowImageUploadModal(false)}
              isVisible={showImageUploadModal}
              handleUpload={imageUploadOptions.handleUpload}
            />,
            document.body
          )}

        {showViewMediaModal &&
          createPortal(
            <ViewMediaModal
              isModalVisible={showViewMediaModal}
              handleClose={() => setShowViewMediaModal(false)}
              {...viewMediaOptions}
            />,
            document.body
          )}
      </>
    );
  }

  return (
    <>
      <section className="section_wrapper">
        <div className="section_title_wrapper">
          <h2 className="section_title">{title ?? `${data.name} details`}</h2>
          {showEditButton && <EditButton onClick={() => setEditable(true)} />}
        </div>
        <div className="row spread">
          <div className="column shrink">
            {data.photo_url ? (
              <div className="photo_wrapper">
                <img src={data.photo_url} alt={data.name} className="photo" />
              </div>
            ) : (
              <div className="photo_wrapper_empty">
                <Icon className="photo_placeholder" icon="iconamoon:profile-fill" />
              </div>
            )}
          </div>
          <div className="section grid_3 full">
            <div className="column">
              {parentName === "Agent" && (
                <div className="row">
                  <span className="label">Agent Company ID</span>
                  <span className="value text_right">{data.id}</span>
                </div>
              )}
              <div className="row">
                <span className="label">{parentName} name (as per Aadhaar)</span>
                <span className="value text_right">{data.name}</span>
              </div>
              <div className="row">
                <span className="label">Contact number</span>
                <span className="value text_right">{data.contact ?? data.mobile}</span>
              </div>
              <div className="row">
                <span className="label">Email ID</span>
                <span className="value text_right">{data.email}</span>
              </div>
              {parentName !== "Agent" && (
                <div className="row">
                  <div className="row">
                    <span className="label">Age</span>
                    <span className="value text_right">{data.age}</span>
                  </div>
                  <div className="row">
                    <span className="label">Gender</span>
                    <span className="value text_right">{data.gender}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="vertical_divider"></div>
            <div className="column">
              {parentName !== "Agent" && (
                <div className="row">
                  <span className="label">Occupation</span>
                  <span className="value text_right">{data.occupation}</span>
                </div>
              )}
              {parentName === "Agent" && (
                <div className="row">
                  <div className="row">
                    <span className="label">Age</span>
                    <span className="value text_right">{data.age}</span>
                  </div>
                  <div className="row">
                    <span className="label">Gender</span>
                    <span className="value text_right">{data.gender}</span>
                  </div>
                </div>
              )}
              <div className="row">
                <span className="label">Address (as per ID)</span>
                <span className="value text_right">{data.address}</span>
              </div>
              <div className="row">
                <span className="label">Aadhaar number</span>
                {data.aadhar_document_url ? (
                  <Button className="ghost_gray" onClick={handleViewAadhar}>
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No image uploaded</span>
                )}
                <Button onClick={handleAadharReupload} className="ghost">
                  Upload
                </Button>
              </div>
              <div className="row">
                <span className="label">PAN number</span>
                {data.pan_document_url ? (
                  <Button className="ghost_gray" onClick={handleViewPan}>
                    preview
                  </Button>
                ) : (
                  <span className="small gray">No image uploaded</span>
                )}
                <Button onClick={handlePanReupload} className="ghost">
                  Upload
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {showImageUploadModal &&
        createPortal(
          <ImageUploadModal
            title={imageUploadOptions.title}
            handleClose={() => setShowImageUploadModal(false)}
            isVisible={showImageUploadModal}
            handleUpload={imageUploadOptions.handleUpload}
          />,
          document.body
        )}

      {showViewMediaModal &&
        createPortal(
          <ViewMediaModal
            isModalVisible={showViewMediaModal}
            handleClose={() => setShowViewMediaModal(false)}
            {...viewMediaOptions}
          />,
          document.body
        )}
    </>
  );
}

export default UserDetails;
